import { create } from "zustand";
import type {
  AgUiStreamEvent,
  NodeStatusUpdate,
  RunResponse,
} from "@/lib/api/runs";
import { API_BASE_URL } from "@/lib/api/client";

const WS_BASE_URL = API_BASE_URL.replace(/^http/, "ws");

export interface AgentStep {
  index: number;
  type: "tool_call" | "tool_result" | "tool_error" | "thinking" | "unknown";
  tool_name?: string;
  tool_args?: Record<string, unknown>;
  content: string | null;
}

export type NodeStatus =
  | "idle"
  | "pending"
  | "running"
  | "completed"
  | "failed"
  | "awaiting_approval";

interface ExecutionStore {
  // State
  runId: string | null;
  runStatus: RunResponse["status"] | null;
  awaitingGateNodeId: string | null;
  nodeStatuses: Record<string, NodeStatus>;
  nodeStartedAt: Record<string, number>; // ms timestamp when node entered "running"
  nodeOutputs: Record<string, Record<string, unknown>>;
  nodeErrors: Record<string, string>;
  nodeErrorTypes: Record<string, string>;
  nodeErrorRetryable: Record<string, boolean>;
  nodeErrorSuggestions: Record<string, string>;
  nodeStreamingText: Record<string, string>;
  agentSteps: Record<string, AgentStep[]>;
  isRunning: boolean;
  runError: string | null;

  // Per-node run callback (set by CanvasPanel)
  onRunNode: ((nodeId: string) => Promise<void>) | null;
  setOnRunNode: (fn: (nodeId: string) => Promise<void>) => void;

  // Approval callbacks (set by CanvasPanel)
  onApprove: (() => Promise<void>) | null;
  onReject: (() => Promise<void>) | null;
  setOnApprove: (fn: () => Promise<void>) => void;
  setOnReject: (fn: () => Promise<void>) => void;

  dismissRunError: () => void;

  // Actions
  startRun: (run: RunResponse) => void;
  /** Sync run-level status from a polled RunResponse (e.g. after WS drops). */
  syncRunStatus: (status: RunResponse["status"]) => void;
  /** Like startRun but preserves existing nodeOutputs/nodeStatuses so upstream
   *  node results remain visible while the resumed run executes downstream nodes. */
  resumeRun: (run: RunResponse) => void;
  cancelRun: () => void;
  reset: () => void;
  /** Seed node outputs/statuses from persisted history on page load. No-op if a run is active. */
  seedLatestOutputs: (data: Record<string, { outputs: Record<string, unknown> }>) => void;

  // Internal
  _ws: WebSocket | null;
  _connectWs: (runId: string) => void;
  _handleStatusUpdate: (update: NodeStatusUpdate) => void;
  _handleStreamEvent: (event: AgUiStreamEvent) => void;
  _handleMessage: (raw: unknown) => void;
  _cleanup: () => void;
}

export const useExecutionStore = create<ExecutionStore>((set, get) => ({
  runId: null,
  runStatus: null,
  awaitingGateNodeId: null,
  nodeStatuses: {},
  nodeStartedAt: {},
  nodeOutputs: {},
  nodeErrors: {},
  nodeErrorTypes: {},
  nodeErrorRetryable: {},
  nodeErrorSuggestions: {},
  nodeStreamingText: {},
  agentSteps: {},
  isRunning: false,
  runError: null,
  onRunNode: null,
  dismissRunError: () => set({ runError: null }),
  setOnRunNode: (fn) => set({ onRunNode: fn }),
  onApprove: null,
  onReject: null,
  setOnApprove: (fn) => set({ onApprove: fn }),
  setOnReject: (fn) => set({ onReject: fn }),
  _ws: null,

  startRun: (run) => {
    const { _cleanup } = get();
    _cleanup();
    set({
      runId: run.id,
      runStatus: run.status,
      awaitingGateNodeId: null,
      nodeStatuses: {},
      nodeStartedAt: {},
      nodeOutputs: {},
      nodeErrors: {},
      nodeErrorTypes: {},
      nodeErrorRetryable: {},
      nodeErrorSuggestions: {},
      nodeStreamingText: {},
      agentSteps: {},
      isRunning: true,
      runError: null,
    });
    get()._connectWs(run.id);
  },

  resumeRun: (run) => {
    const { _cleanup, awaitingGateNodeId, nodeStatuses } = get();
    _cleanup();
    // Preserve existing node outputs/statuses so upstream results stay visible.
    // Mark the gate node as "completed" so it stops showing "Awaiting approval".
    set({
      runId: run.id,
      runStatus: run.status,
      awaitingGateNodeId: null,
      isRunning: true,
      nodeStatuses: awaitingGateNodeId
        ? { ...nodeStatuses, [awaitingGateNodeId]: "completed" }
        : nodeStatuses,
    });
    get()._connectWs(run.id);
  },

  cancelRun: () => {
    const { _cleanup, awaitingGateNodeId, nodeStatuses } = get();
    _cleanup();
    set({
      isRunning: false,
      runStatus: "cancelled",
      awaitingGateNodeId: null,
      nodeStreamingText: {},
      // Mark the gate node as failed so it stops rendering action buttons
      nodeStatuses: awaitingGateNodeId
        ? { ...nodeStatuses, [awaitingGateNodeId]: "failed" }
        : nodeStatuses,
    });
  },

  reset: () => {
    const { _cleanup } = get();
    _cleanup();
    set({
      runId: null,
      runStatus: null,
      awaitingGateNodeId: null,
      nodeStatuses: {},
      nodeStartedAt: {},
      nodeOutputs: {},
      nodeErrors: {},
      nodeErrorTypes: {},
      nodeErrorRetryable: {},
      nodeErrorSuggestions: {},
      nodeStreamingText: {},
      agentSteps: {},
      isRunning: false,
      runError: null,
    });
  },

  syncRunStatus: (status) => {
    const terminalStatuses = ["completed", "failed", "cancelled"];
    set({
      runStatus: status,
      isRunning: !terminalStatuses.includes(status),
    });
  },

  seedLatestOutputs: (data) => {
    if (get().isRunning) return;
    const nodeStatuses: Record<string, NodeStatus> = {};
    const nodeOutputs: Record<string, Record<string, unknown>> = {};
    for (const [nodeId, { outputs }] of Object.entries(data)) {
      nodeStatuses[nodeId] = "completed";
      nodeOutputs[nodeId] = outputs;
    }
    set({ nodeStatuses, nodeOutputs });
  },

  _connectWs: (runId) => {
    const ws = new WebSocket(`${WS_BASE_URL}/ws/runs/${runId}`);

    ws.onmessage = (event) => {
      try {
        const raw = JSON.parse(event.data);
        get()._handleMessage(raw);
      } catch (e) {
        console.warn(
          "execution-store: failed to parse WebSocket message:",
          event.data,
          e,
        );
      }
    };

    ws.onclose = () => {
      set({ _ws: null });
    };

    set({ _ws: ws });
  },

  _handleMessage: (raw) => {
    const msg = raw as Record<string, unknown>;
    // Agent step events
    if (msg.type === "agent_step" && typeof msg.nodeId === "string") {
      const { nodeId, step } = msg as { nodeId: string; step: AgentStep };
      set((state) => ({
        agentSteps: {
          ...state.agentSteps,
          [nodeId]: [...(state.agentSteps[nodeId] ?? []), step],
        },
      }));
      return;
    }
    // AG-UI streaming events have type "TEXT_MESSAGE_*"; status messages have "status" field
    if (
      typeof msg.type === "string" &&
      msg.type.startsWith("TEXT_MESSAGE_") &&
      typeof msg.nodeId === "string"
    ) {
      get()._handleStreamEvent(msg as unknown as AgUiStreamEvent);
    } else {
      get()._handleStatusUpdate(msg as unknown as NodeStatusUpdate);
    }
  },

  _handleStreamEvent: (event) => {
    const { nodeId, type, delta } = event;
    if (type === "TEXT_MESSAGE_START") {
      set((state) => ({
        nodeStreamingText: { ...state.nodeStreamingText, [nodeId]: "" },
      }));
    } else if (type === "TEXT_MESSAGE_CONTENT" && delta) {
      set((state) => ({
        nodeStreamingText: {
          ...state.nodeStreamingText,
          [nodeId]: (state.nodeStreamingText[nodeId] ?? "") + delta,
        },
      }));
    }
    // TEXT_MESSAGE_END: no-op — streaming text is cleared by _handleStatusUpdate when outputs arrive
  },

  _handleStatusUpdate: (update) => {
    if (update.nodeId) {
      set((state) => ({
        nodeStatuses: {
          ...state.nodeStatuses,
          [update.nodeId!]: update.status as NodeStatus,
        },
        ...(update.status === "running"
          ? {
              nodeStartedAt: {
                ...state.nodeStartedAt,
                [update.nodeId!]: Date.now(),
              },
            }
          : {}),
        ...(update.outputs
          ? {
              nodeOutputs: {
                ...state.nodeOutputs,
                [update.nodeId!]: update.outputs!,
              },
              // Clear streaming text once final outputs arrive
              nodeStreamingText: {
                ...state.nodeStreamingText,
                [update.nodeId!]: "",
              },
            }
          : {}),
        ...(update.error
          ? {
              nodeErrors: {
                ...state.nodeErrors,
                [update.nodeId!]: update.error!,
              },
              ...(update.error_type
                ? {
                    nodeErrorTypes: {
                      ...state.nodeErrorTypes,
                      [update.nodeId!]: update.error_type,
                    },
                  }
                : {}),
              ...(typeof update.retryable === "boolean"
                ? {
                    nodeErrorRetryable: {
                      ...state.nodeErrorRetryable,
                      [update.nodeId!]: update.retryable,
                    },
                  }
                : {}),
              ...(update.suggestion
                ? {
                    nodeErrorSuggestions: {
                      ...state.nodeErrorSuggestions,
                      [update.nodeId!]: update.suggestion,
                    },
                  }
                : {}),
            }
          : {}),
      }));
    }

    if (update.runId) {
      const terminalStatuses = ["completed", "failed", "cancelled"];
      const raw = update as unknown as Record<string, unknown>;
      const patch: Partial<ExecutionStore> = {
        runStatus: update.status as RunResponse["status"],
        isRunning: !terminalStatuses.includes(update.status),
      };
      if (update.status === "awaiting_approval" && typeof raw.awaitingNodeId === "string") {
        patch.awaitingGateNodeId = raw.awaitingNodeId;
      }
      if (update.status === "failed" && typeof raw.error === "string") {
        patch.runError = raw.error;
      }
      set(patch);
    }
  },

  _cleanup: () => {
    const { _ws } = get();
    if (
      _ws &&
      (_ws.readyState === WebSocket.OPEN ||
        _ws.readyState === WebSocket.CONNECTING)
    ) {
      _ws.close();
    }
    set({ _ws: null });
  },
}));
